import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import './UtilizationDiagnostic.css';

/**
 * In-App Utilization Diagnostic Component
 * 
 * This component runs within your React app using your existing admin authentication
 * to diagnose why material utilization is showing 0.
 */
const UtilizationDiagnostic = () => {
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const runDiagnostic = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Running utilization diagnostic...');
      
      // Check inventory data
      const inventoryData = await checkInventoryData();
      
      // Check invoice data
      const invoiceData = await checkInvoiceData();
      
      // Check material lists
      const materialListData = await checkMaterialListData();
      
      // Analyze the data
      const analysis = analyzeUtilizationData(inventoryData, invoiceData, materialListData);
      
      setDiagnosticData({
        inventory: inventoryData,
        invoices: invoiceData,
        materialLists: materialListData,
        analysis,
        timestamp: new Date().toISOString()
      });
      
    } catch (err) {
      console.error('❌ Diagnostic error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkInventoryData = async () => {
    try {
      const inventoryQuery = query(
        collection(db, 'inventory'),
        orderBy('createdAt', 'desc'),
        limit(50) // Get first 50 items for analysis
      );
      
      const snapshot = await getDocs(inventoryQuery);
      const items = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
      
      // Analyze status values
      const statusCounts = {};
      const materialTypes = {};
      
      items.forEach(item => {
        const status = item.status || 'no_status';
        const materialType = item.materialType || item.type || 'no_type';
        
        statusCounts[status] = (statusCounts[status] || 0) + 1;
        materialTypes[materialType] = (materialTypes[materialType] || 0) + 1;
      });
      
      return {
        totalItems: items.length,
        items: items.slice(0, 5), // Show first 5 for inspection
        statusCounts,
        materialTypes,
        hasData: items.length > 0
      };
      
    } catch (error) {
      console.error('❌ Error checking inventory:', error);
      return {
        totalItems: 0,
        items: [],
        statusCounts: {},
        materialTypes: {},
        hasData: false,
        error: error.message
      };
    }
  };

  const checkInvoiceData = async () => {
    try {
      const invoiceQuery = query(
        collection(db, 'invoices'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const snapshot = await getDocs(invoiceQuery);
      const invoices = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
      
      return {
        totalInvoices: invoices.length,
        invoices: invoices.slice(0, 3), // Show first 3 for inspection
        hasData: invoices.length > 0
      };
      
    } catch (error) {
      console.error('❌ Error checking invoices:', error);
      return {
        totalInvoices: 0,
        invoices: [],
        hasData: false,
        error: error.message
      };
    }
  };

  const checkMaterialListData = async () => {
    try {
      const materialListQuery = query(
        collection(db, 'materialLists'),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      
      const snapshot = await getDocs(materialListQuery);
      const materialLists = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt
      }));
      
      return {
        totalMaterialLists: materialLists.length,
        materialLists: materialLists.slice(0, 3),
        hasData: materialLists.length > 0
      };
      
    } catch (error) {
      console.error('❌ Error checking material lists:', error);
      return {
        totalMaterialLists: 0,
        materialLists: [],
        hasData: false,
        error: error.message
      };
    }
  };

  const analyzeUtilizationData = (inventoryData, invoiceData, materialListData) => {
    const issues = [];
    const recommendations = [];
    
    // Check if inventory exists
    if (!inventoryData.hasData) {
      issues.push({
        type: 'critical',
        title: 'No Inventory Data',
        description: 'No inventory items found. Material utilization requires inventory items to calculate metrics.',
        impact: 'Utilization rate will be 0%'
      });
      
      recommendations.push({
        priority: 'high',
        action: 'Add Inventory Items',
        description: 'Create inventory items with proper materialType and status fields.',
        steps: [
          'Go to Inventory Dashboard',
          'Add new inventory items',
          'Ensure each item has a materialType and status field',
          'Use status values: available, deployed, damaged, maintenance'
        ]
      });
    } else {
      // Analyze status values
      const { statusCounts } = inventoryData;
      const expectedStatuses = ['deployed', 'in_use'];
      const deployedCount = expectedStatuses.reduce((sum, status) => sum + (statusCounts[status] || 0), 0);
      
      if (deployedCount === 0) {
        const actualStatuses = Object.keys(statusCounts);
        
        issues.push({
          type: 'warning',
          title: 'No Deployed Items Found',
          description: `Found ${inventoryData.totalItems} inventory items, but none have status 'deployed' or 'in_use'.`,
          impact: 'Utilization rate will be 0%',
          details: {
            expectedStatuses: expectedStatuses,
            actualStatuses: actualStatuses,
            statusCounts: statusCounts
          }
        });
        
        // Check if there are similar status values
        const similarStatuses = actualStatuses.filter(status => 
          status.includes('rent') || 
          status.includes('out') || 
          status.includes('site') || 
          status.includes('active') ||
          status.includes('use')
        );
        
        if (similarStatuses.length > 0) {
          recommendations.push({
            priority: 'medium',
            action: 'Update Status Values',
            description: `Found similar status values: ${similarStatuses.join(', ')}. Update these to 'deployed' or 'in_use'.`,
            steps: [
              'Go to Inventory Dashboard',
              'Find items with status: ' + similarStatuses.join(', '),
              'Update their status to "deployed" or "in_use"',
              'Refresh analytics dashboard'
            ]
          });
        } else {
          recommendations.push({
            priority: 'medium',
            action: 'Set Items as Deployed',
            description: 'Mark some inventory items as deployed to show utilization.',
            steps: [
              'Go to Inventory Dashboard',
              'Select items that are currently in use',
              'Change their status to "deployed"',
              'Refresh analytics dashboard'
            ]
          });
        }
      } else {
        issues.push({
          type: 'success',
          title: 'Deployed Items Found',
          description: `Found ${deployedCount} deployed items out of ${inventoryData.totalItems} total items.`,
          impact: `Expected utilization rate: ${((deployedCount / inventoryData.totalItems) * 100).toFixed(1)}%`
        });
      }
    }
    
    // Check invoice vs inventory relationship
    if (invoiceData.hasData && !inventoryData.hasData) {
      issues.push({
        type: 'warning',
        title: 'Invoice-Inventory Mismatch',
        description: `Found ${invoiceData.totalInvoices} invoices but no inventory items.`,
        impact: 'Cannot calculate material utilization without inventory data'
      });
      
      recommendations.push({
        priority: 'high',
        action: 'Create Inventory from Invoices',
        description: 'Generate inventory items based on materials mentioned in your invoices.',
        steps: [
          'Review invoice line items to identify materials',
          'Create inventory items for each material type',
          'Set appropriate quantities and status values',
          'Link inventory to actual physical items'
        ]
      });
    }
    
    return {
      issues,
      recommendations,
      summary: {
        hasInventory: inventoryData.hasData,
        hasInvoices: invoiceData.hasData,
        hasMaterialLists: materialListData.hasData,
        canCalculateUtilization: inventoryData.hasData && Object.keys(inventoryData.statusCounts).some(status => 
          ['deployed', 'in_use'].includes(status)
        )
      }
    };
  };

  const getStatusColor = (type) => {
    switch (type) {
      case 'critical': return '#ff4d4f';
      case 'warning': return '#faad14';
      case 'success': return '#52c41a';
      case 'info': return '#1890ff';
      default: return '#666';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#666';
    }
  };

  return (
    <div className="utilization-diagnostic">
      <div className="diagnostic-header">
        <h2>🔍 Material Utilization Diagnostic</h2>
        <p>This diagnostic checks why your material utilization is showing 0</p>
        <button 
          onClick={runDiagnostic} 
          disabled={loading}
          className="diagnostic-button"
        >
          {loading ? '🔄 Running Diagnostic...' : '🚀 Run Diagnostic'}
        </button>
      </div>

      {error && (
        <div className="diagnostic-error">
          <h3>❌ Error</h3>
          <p>{error}</p>
        </div>
      )}

      {diagnosticData && (
        <div className="diagnostic-results">
          <div className="diagnostic-timestamp">
            <small>Diagnostic run at: {new Date(diagnosticData.timestamp).toLocaleString()}</small>
          </div>

          {/* Data Summary */}
          <div className="diagnostic-section">
            <h3>📊 Data Summary</h3>
            <div className="data-summary">
              <div className="summary-item">
                <span className="summary-label">Inventory Items:</span>
                <span className="summary-value">{diagnosticData.inventory.totalItems}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Invoices:</span>
                <span className="summary-value">{diagnosticData.invoices.totalInvoices}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Material Lists:</span>
                <span className="summary-value">{diagnosticData.materialLists.totalMaterialLists}</span>
              </div>
            </div>
          </div>

          {/* Issues */}
          <div className="diagnostic-section">
            <h3>🚨 Issues Found</h3>
            {diagnosticData.analysis.issues.map((issue, index) => (
              <div key={index} className="issue-item" style={{ borderLeft: `4px solid ${getStatusColor(issue.type)}` }}>
                <div className="issue-header">
                  <span className="issue-type" style={{ color: getStatusColor(issue.type) }}>
                    {issue.type.toUpperCase()}
                  </span>
                  <h4>{issue.title}</h4>
                </div>
                <p>{issue.description}</p>
                <div className="issue-impact">
                  <strong>Impact:</strong> {issue.impact}
                </div>
                {issue.details && (
                  <div className="issue-details">
                    <strong>Details:</strong>
                    <pre>{JSON.stringify(issue.details, null, 2)}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Inventory Status Breakdown */}
          {diagnosticData.inventory.hasData && (
            <div className="diagnostic-section">
              <h3>📦 Inventory Status Breakdown</h3>
              <div className="status-breakdown">
                {Object.entries(diagnosticData.inventory.statusCounts).map(([status, count]) => (
                  <div key={status} className="status-item">
                    <span className="status-name">{status}:</span>
                    <span className="status-count">{count} items</span>
                    <span className="status-percentage">
                      ({((count / diagnosticData.inventory.totalItems) * 100).toFixed(1)}%)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sample Inventory Items */}
          {diagnosticData.inventory.items.length > 0 && (
            <div className="diagnostic-section">
              <h3>🔍 Sample Inventory Items</h3>
              <div className="sample-items">
                {diagnosticData.inventory.items.map((item, index) => (
                  <div key={index} className="sample-item">
                    <div className="item-header">
                      <strong>Item {index + 1}:</strong> {item.materialType || item.type || 'Unknown Type'}
                    </div>
                    <div className="item-details">
                      <span className="item-status">Status: {item.status || 'No Status'}</span>
                      <span className="item-id">ID: {item.id}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div className="diagnostic-section">
            <h3>💡 Recommendations</h3>
            {diagnosticData.analysis.recommendations.map((rec, index) => (
              <div key={index} className="recommendation-item">
                <div className="recommendation-header">
                  <span className="recommendation-priority" style={{ color: getPriorityColor(rec.priority) }}>
                    {rec.priority.toUpperCase()} PRIORITY
                  </span>
                  <h4>{rec.action}</h4>
                </div>
                <p>{rec.description}</p>
                <div className="recommendation-steps">
                  <strong>Steps:</strong>
                  <ol>
                    {rec.steps.map((step, stepIndex) => (
                      <li key={stepIndex}>{step}</li>
                    ))}
                  </ol>
                </div>
              </div>
            ))}
          </div>

          {/* Utilization Calculation Preview */}
          {diagnosticData.inventory.hasData && (
            <div className="diagnostic-section">
              <h3>📈 Utilization Calculation Preview</h3>
              <div className="calculation-preview">
                <div className="calculation-formula">
                  <strong>Formula:</strong> Utilization Rate = (Deployed Items / Total Items) × 100
                </div>
                <div className="calculation-values">
                  <div>Total Items: {diagnosticData.inventory.totalItems}</div>
                  <div>Deployed Items: {(diagnosticData.inventory.statusCounts.deployed || 0) + (diagnosticData.inventory.statusCounts.in_use || 0)}</div>
                  <div>Current Utilization Rate: {
                    diagnosticData.inventory.totalItems > 0 
                      ? (((diagnosticData.inventory.statusCounts.deployed || 0) + (diagnosticData.inventory.statusCounts.in_use || 0)) / diagnosticData.inventory.totalItems * 100).toFixed(1)
                      : 0
                  }%</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UtilizationDiagnostic;
